<template>
  <v-card class="px-8 py-8">
    <v-form id="rates-rules-dialog" ref="ratesForm">
      <v-row>
        <v-col cols="12" class="pt-0">
          <h2 class="font-weight-regular pb-4 black--text">Rate Details</h2>
        </v-col>
      </v-row>
      <v-row class="px-4">
        <v-col cols="12" class="pt-0 px-0 pb-0">
          <h2 class="subtitle-1 font-weight-regular">Rate name <span class="red--text">*</span></h2>
          <v-text-field v-model="mutatableRate.description" class="mt-4" solo-flat outlined dense :rules="descriptionRules" placeholder="Name"></v-text-field>
        </v-col>
      </v-row>
      <v-row class="px-4">
          <v-col cols="12" class="pt-0 px-0 pb-0">
            <span class="subtitle-1 font-weight-regular">Rate period</span>
            <v-row>
              <v-col :class="{'py-0': !rateDates.length}">
                <div v-for="(date, dateIndex) in rateDates" :key="date.id" class="mb-2">
                  <v-chip close class="mr-2" @click:close="removeDateFromRule(dateIndex)">{{ moment(date.dates.start).format('DD/MM/YYYY') }} - {{ moment(date.dates.end).format('DD/MM/YYYY') }}</v-chip>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="5">
                <v-menu v-model="fromDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :label="$t('page-rules.seasonal-rule.section-details.fields.season-period.from.label')"
                      :value="fromDateFormatted"
                      append-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    no-title
                    :allowed-dates="allowedDates"
                    v-model="fromDate"
                    @change="fromDateMenu = false; toDate = moment(fromDate).format('YYYY-MM-DD')"
                    :min="moment(new Date()).format('YYYY-MM-DD')"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="5">
                <v-menu v-model="toDateMenu" :close-on-content-click="false" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                    :label="$t('page-rules.seasonal-rule.section-details.fields.season-period.to.label')"
                      :value="toDateFormatted"
                      append-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      :disabled="!fromDate"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    no-title
                    v-model="toDate"
                    @change="toDateMenu = false"
                    :allowed-dates="allowedDatesFromStart"
                    :min="moment(fromDate).format('YYYY-MM-DD')"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="2">
                <v-btn color="primary" :disabled="!fromDate || !toDate" outlined @click="addDateToRule()" style="padding-top:19px; padding-bottom: 19px;">Add Date</v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
    </v-form>

      <div>
        <hr role="separator" aria-orientation="horizontal" class="v-divider theme--light pb-10">
        <h2 class="font-weight-regular black--text pb-4">Pricing rules <span class="red--text">*</span></h2>
        <p class="mb-0">You must activate at least one of the pricing rules below</p>
      </div>

    <v-expansion-panels v-model="panel" multiple flat>
      <v-expansion-panel @click="switch1 = !switch1" class="py-10 border-t-0 ml-8">
        <v-expansion-panel-header class="px-0" disable-icon-rotate>
          <div>
            <h3 class="font-weight-regular black--text pb-4">Price per night</h3>
            <p class="mb-0 text--secondary">Set a nightly rate depending on the day of the week</p>
          </div>
          <template v-slot:actions>
            <v-switch :input-value="switch1" inset></v-switch>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-form ref="ppnForm">
            <v-row class="mt-5" justify="space-between" no-gutters>
              <v-col style="display: flex;align-items: baseline;" cols="12" class="pt-0">
                <div style="display: flex;justify-content: center;flex-direction: column;align-items: center;">
                  <span class="pb-1">Everyday</span>
                  <div class="pr-5" style="display: flex;align-items: baseline;">
                    <span style="padding-right: 10px;">£</span>
                    <v-text-field @input="addRate($event)" type="number" min="0" outlined :disabled="dayOfWeekRatesSet" :value="mutatableRate.rate"></v-text-field>
                  </div>
                </div>
                <p class="px-4 font-italic">or</p>
                <div v-for="(day, i) in days" :key="day" style="display: flex;justify-content: center;flex-direction: column;align-items: center;">
                  <span class="pb-1">{{ day }}</span>
                  <div class="pr-5" style="display: flex;align-items: baseline;">
                    <span style="padding-right: 10px;">£</span>
                    <v-text-field @input="addRates($event, i)" @blur="setValues($event, i)" type="number" min="0" outlined :disabled="rateSet" :value="mutatableRate.dayOfWeekRates[i]" :rules="dayOfWeekRatesRules"></v-text-field>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel @click="switch2 = !switch2" class="py-10 border-opacity-60 ml-8">
        <v-expansion-panel-header class="px-0" disable-icon-rotate>
          <div>
            <h3 class="font-weight-regular pb-4 black--text">Length Of Stay</h3>
            <p class="mb-0 text--secondary">Set different rates for bookings, depending on different number or night</p>
          </div>
          <template v-slot:actions>
            <v-switch :input-value="switch2" inset></v-switch>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-radio-group row v-model="radioGroup" @change="resetLOSForms">
            <v-radio :value="true" :label="'Exactly (' + exactRateLos.length + ')'"></v-radio>
            <v-radio :value="false" :label="'Between (' + betweenRateLos.length + ')'"></v-radio>
          </v-radio-group>

          <v-row v-show="radioGroup" justify="space-between" no-gutters>
            <div v-show="exactRateLos.length > 0">
              <v-banner outlined v-for="rlos in exactRateLos" class="mb-5" :key="rlos.lineItemId">
                Checking-in on <span class="font-weight-bold">{{ checkInDays[rlos.checkInDay] ? checkInDays[rlos.checkInDay].text : null }}</span
                >, for exactly <span class="font-weight-bold">{{ rlos.minLOS }}</span> nights at £<span class="font-weight-bold">{{ rlos.price }}</span> per stay
                <button @click.prevent="deleteRateLos(rlos.lineItemId)" type="button" class="px-2 py-1 white--text">
                  <v-icon medium color="gray darken-2">
                    mdi-close-circle
                  </v-icon>
                </button>
              </v-banner>
            </div>
            <v-form ref="exactForm">
              <v-col class="rate-los pt-0" cols="12">
                <div class="rate-los__field-group">
                  <span>Checking-in on</span>
                  <v-select class="select" v-model="rateLengthOfStay.checkInDay" item-value="value" item-text="text" :items="checkInDays" outlined></v-select>
                </div>

                <div class="pr-5 rate-los__field-group">
                  <span>for exactly</span>
                  <v-text-field class="num" ref="minLos" v-model.number="rateLengthOfStay.minLOS" type="number" min="1" max="28" outlined :rules="minLosRules"></v-text-field>
                  <span>nights(s)</span>
                </div>

                <div class="pr-5 rate-los__field-group">
                  <span>at £</span>
                  <v-text-field class="num" ref="price" v-model.number="rateLengthOfStay.price" type="number" min="0" outlined :rules="pricePerStayRules"></v-text-field>
                  <span>per stay</span>
                </div>

                <v-btn color="primary" outlined @click.prevent="addRateLos(RATE_LOS_DURATION_TYPES_KEYS.STAY, 'exactForm')" type="button">
                  save
                </v-btn>
              </v-col>
            </v-form>
          </v-row>

          <v-row v-show="!radioGroup" justify="space-between" no-gutters>
            <div v-show="betweenRateLos.length > 0">
              <v-banner outlined v-for="rlos in betweenRateLos" class="mb-5" :key="rlos.lineItemId">
                Checking-in on <span class="font-weight-bold">{{ checkInDays[rlos.checkInDay] ? checkInDays[rlos.checkInDay].text : null }}</span
                >, between <span class="font-weight-bold">{{ rlos.minLOS }}</span> & <span class="font-weight-bold">{{ rlos.maxLOS }}</span> nights at £<span class="font-weight-bold">{{ rlos.price }}</span> per night
                <button @click.prevent="deleteRateLos(rlos.lineItemId)" type="button" class="px-2 py-1 white--text">
                  <v-icon medium color="gray darken-2">
                    mdi-close-circle
                  </v-icon>
                </button>
              </v-banner>
            </div>
            <v-form ref="betweenForm">
              <v-col class="rate-los pt-0" cols="12">
                <div class="pr-3 rate-los__field-group">
                  <span>Checking-in on</span>
                  <v-select :items="checkInDays" item-value="value" item-text="text" outlined v-model="rateLengthOfStay.checkInDay"></v-select>
                </div>

                <div class="pr-3 rate-los__field-group">
                  <span>between</span>
                  <v-text-field class="num" type="number" min="1" max="28" outlined :rules="minLosRules" v-model.number="rateLengthOfStay.minLOS"></v-text-field>
                  <span>&</span>
                  <v-text-field class="num" type="number" min="1" max="28" outlined :rules="maxLosRules" v-model.number="rateLengthOfStay.maxLOS"></v-text-field>
                  <span>nights(s)</span>
                </div>

                <div class="pr-3 rate-los__field-group">
                  <span>at £</span>
                  <v-text-field class="num" type="number" min="0" outlined :rules="pricePerNightRules" v-model.number="rateLengthOfStay.price"></v-text-field>
                  <span>per night</span>
                </div>

                <button @click.prevent="addRateLos(RATE_LOS_DURATION_TYPES_KEYS.NIGHT, 'betweenForm')" type="button">
                  <v-icon medium color="gray darken-2">
                    mdi-plus-circle
                  </v-icon>
                </button>
              </v-col>
            </v-form>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-divider></v-divider>

    <v-row class="pt-12">
      <v-col cols="12" class="pt-0">
        <h2 class="font-weight-regular black--text pb-4">Select Places/Rooms</h2>
        <p class="mb-0">Which places/rooms would you like to apply the rates to?</p>
      </v-col>
    </v-row>
    <v-row class="pb-3">
      <v-col cols="12">
        <v-autocomplete v-model="assignedRooms" :items="units" outlined chips label="Search a room..." multiple></v-autocomplete>
      </v-col>
    </v-row>

    <v-alert outlined type="warning" text class="mb-2 text-left" v-if="!ppnOrLos">*You cannot finalise this rate until it has a name, 'price per night' and/or at least one or more 'length of stay(s)' applied.*</v-alert>

    <div class="text-right">
      <v-btn :disabled="!isValid || saving" @click.stop="save()" right color="primary" large v-text="buttonText" :loading="saving" />
    </div>
  </v-card>
  </template>

<script>
import { mapActions, mapState } from 'pinia'
import { useRateStore, useBookableStore, useUnitStore } from '@/stores'
import { RATE_LOS_DURATION_TYPES_KEYS } from '@/constants'
import { clone } from 'ramda'
import moment from 'moment'
import { sortObjects } from '@/helpers/stateUtils'

export default {
  props: {
    place: {
      type: Object,
      required: true,
    },
    isCreate: {
      type: Boolean,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: true,
    },
    isDuplicate: {
      type: Boolean,
      required: false,
      default: false,
    },
    rate: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      RATE_LOS_DURATION_TYPES_KEYS,
      panel: [],
      mutatableRate: this.clone(this.rate),
      saving: false,
      fromDateMenu: false,
      toDateMenu: false,
      fromDate: null,
      toDate: null,
      assignedRooms: [],
      rateLengthOfStays: [],
      rateLengthOfStay: {
        checkInDay: 0,
        minLOS: 0,
        maxLOS: 0,
        price: 0,
      },
      switch1: false,
      switch2: false,
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      checkInDays: [
        { text: 'any day', value: 0 },
        { text: 'Monday', value: 1 },
        { text: 'Tuesday', value: 2 },
        { text: 'Wednesday', value: 3 },
        { text: 'Thursday', value: 4 },
        { text: 'Friday', value: 5 },
        { text: 'Saturday', value: 6 },
        { text: 'Sunday', value: 7 },
      ],
      radioGroup: true,
      descriptionRules: [(v) => !!v || 'Description is required.'],
      minLosRules: [(v) => !!v || 'Minimum length of stay is required.', (v) => (!!v && v <= 28) || 'Minimum length of stay cannot be greater than 28'],
      maxLosRules: [
        (v) => !!v || 'Maximum length of stay is required.',
        (v) => {
          return (!!v && this.rateLengthOfStay.minLOS < this.rateLengthOfStay.maxLOS) || 'Maximum length of stay is less than the minimum'
        },
        (v) => (!!v && v <= 28) || 'Maximum length of stay cannot be greater than 28',
      ],
      pricePerStayRules: [(v) => !!v || 'A price per-stay is required.'],
      pricePerNightRules: [(v) => !!v || 'A price per-night is required.'],
      dayOfWeekRatesRules: [
        (v) => {
          return !this.dayOfWeekRatesSet || (this.dayOfWeekRatesSet && v > 0) || 'All days are required when setting a price for each night.'
        },
      ],
    }
  },
  computed: {
    ...mapState(useRateStore, {
      rateFindByIds: 'findByIds',
    }),
    isValid () {
      return !!(this.mutatableRate.description && this.ppnOrLos)
    },
    title () {
      let title
      if (this.isCreate && !this.isDuplicate) {
        title = 'Add Rate'
      } else if (this.isDuplicate) {
        title = 'Duplicate Rate'
      } else {
        title = 'Edit Rate'
      }
      return title
    },
    subTitle () {
      return this.title + ' for your spaces. You must provide a rate, whether via the Price Per Night or Length of Stay sections.*'
    },
    buttonText () {
      let title
      if (this.isCreate && !this.isDuplicate) {
        title = 'Create Rate'
      } else if (this.isEdit) {
        title = 'Edit Rate'
      } else {
        title = 'Duplicate Rate'
      }

      return title
    },
    fromDateFormatted () {
      return this.fromDate ? moment(this.fromDate).format('DD/MM/Y') : ''
    },
    toDateFormatted () {
      return this.toDate ? moment(this.toDate).format('DD/MM/Y') : ''
    },
    rateDates () {
      return this.sortObjects(this.mutatableRate.rateDates, 'dates.start')
    },
    placeRateDatesFlattened () {
      let flattenedDates = []

      // dates under this rule (done separately so we capture real-time events)
      this.mutatableRate.rateDates.forEach(rrd => {
        let start = moment(rrd.dates.start)
        let end = moment(rrd.dates.end)
        let diff = end.diff(start, 'days')

        // add each day in range to array
        for (let i = 0; i <= diff; i++) {
          flattenedDates.push(start.clone().add(i, 'd').format('YYYY-MM-DD'))
        }
      })

      // all other seasonal booking rules under this place
      this.rateFindByIds(this.place.rates).forEach(rate => {
        if (rate.id !== this.mutatableRate.id) {
          rate.rateDates.forEach(rrd => {
            let start = moment(rrd.dates.start)
            let end = moment(rrd.dates.end)
            let diff = end.diff(start, 'days')

            // add each day in range to array
            for (let i = 0; i <= diff; i++) {
              flattenedDates.push(start.clone().add(i, 'd').format('YYYY-MM-DD'))
            }
          })
        }
      })

      return flattenedDates
    },
    rateSet () {
      if (this.mutatableRate.rate > 0) {
        return true
      } else {
        return false
      }
    },
    dayOfWeekRatesSet () {
      const total = this.mutatableRate.dayOfWeekRates.reduce(function (total, day) {
        return total + day[0]
      }, 0)

      return total > 0
    },
    exactRateLos () {
      return this.rateLengthOfStays
        .filter((item) => item.durationType === RATE_LOS_DURATION_TYPES_KEYS.STAY)
        .map((los) => {
          if (los.id) {
            los.lineItemId = los.id
          }
          return los
        })
    },
    betweenRateLos () {
      return this.rateLengthOfStays
        .filter((item) => item.durationType === RATE_LOS_DURATION_TYPES_KEYS.NIGHT)
        .map((los) => {
          if (los.id) {
            los.lineItemId = los.id
          }
          return los
        })
    },
    ...mapState(useRateStore, {
      rateFindById: 'findById',
    }),
    ...mapState(useUnitStore, {
      unitsFindByIds: 'findByIds',
    }),
    ...mapState(useBookableStore, {
      bookablesFindByIds: 'findByIds',
    }),
    units () {
      const placesRoomsItem = []
      this.bookablesFindByIds(this.place.bookables).map((bookable) => {
        placesRoomsItem.push({ header: bookable.name })
        this.unitsFindByIds(bookable.units).map((unit) => {
          placesRoomsItem.push({
            text: unit.name,
            value: unit['@id'],
          })
        })
      })
      return placesRoomsItem
    },
    ppnOrLos () {
      const total = this.mutatableRate.dayOfWeekRates.reduce(function (total, day) {
        return total + day[0]
      }, 0)

      return !!(this.rateLengthOfStays.length > 0 || this.mutatableRate.rate > 0 || total > 0)
    },
  },
  methods: {
    sortObjects,
    clone,
    ...mapActions(useRateStore, ['httpCreateRate', 'httpUpdateRate']),
    allowedDates (date) {
      return !this.placeRateDatesFlattened.includes(date)
    },
    allowedDatesFromStart (date) {
      let start = moment(this.fromDate)
      let end = moment(date)
      let diff = end.diff(start, 'days')

      // loop through each day since start date and return false at the first occurence of an unavailable date
      for (let i = 1; i <= diff; i++) {
        let incDay = start.clone().add(i, 'd')
        if (this.placeRateDatesFlattened.includes(incDay.format('YYYY-MM-DD'))) {
          return false
        }
      }

      return true
    },
    addDateToRule () {
      this.mutatableRate.rateDates.push({
        dates: {
          start: this.fromDate,
          end: this.toDate,
        },
      })

      this.fromDate = null
      this.toDate = null
      this.$refs.ratesForm.resetValidation()
    },
    removeDateFromRule (dateIndex) {
      this.mutatableRate.rateDates.splice(dateIndex, 1)
    },
    resetLOSForms () {
      if (this.$refs.exactForm) {
        this.$refs.exactForm.resetValidation()
      }
      if (this.$refs.betweenForm) {
        this.$refs.betweenForm.resetValidation()
      }
      this.rateLengthOfStay = {
        checkInDay: 0,
        minLOS: 0,
        maxLOS: 0,
        price: 0,
      }
    },
    setValues (event, day) {
      if (event.target.value === '') {
        const clone = this.clone(this.mutatableRate)
        clone.dayOfWeekRates[day][0] = 0
        this.mutatableRate = clone
        this.$refs.ppnForm.validate()
      }
    },
    addRateLos (durationType, form) {
      if (this.$refs[form].validate()) {
        this.rateLengthOfStay.lineItemId = Math.random()
          .toString()
          .substr(2, 8) // to be replaced by BEAM generated ID
        this.rateLengthOfStay.durationType = durationType

        if (durationType === RATE_LOS_DURATION_TYPES_KEYS.STAY) {
          this.rateLengthOfStay.maxLOS = this.rateLengthOfStay.minLOS
        }

        const cloneRateLengthOfStay = this.clone(this.rateLengthOfStay)
        cloneRateLengthOfStay.minLOS = parseInt(cloneRateLengthOfStay.minLOS)
        cloneRateLengthOfStay.maxLOS = parseInt(cloneRateLengthOfStay.maxLOS)
        cloneRateLengthOfStay.price = parseFloat(cloneRateLengthOfStay.price)

        this.rateLengthOfStays.push(cloneRateLengthOfStay)

        // reset rate length of stay back to default
        this.resetLOSForms()
      }
    },
    deleteRateLos (id) {
      const i = this.rateLengthOfStays.findIndex((rlos) => rlos.lineItemId === id)

      if (i > -1) {
        this.rateLengthOfStays.splice(i, 1)
      }
    },
    addRate (event) {
      const clone = this.clone(this.mutatableRate)
      clone.rate = parseFloat(event) || 0
      // reset any day of week rates back
      clone.dayOfWeekRates = [[0], [0], [0], [0], [0], [0], [0]]
      this.mutatableRate = clone
    },
    addRates (event, day) {
      const clone = this.clone(this.mutatableRate)
      clone.dayOfWeekRates[day][0] = parseFloat(event) || 0
      this.mutatableRate = clone

      if (!this.dayOfWeekRatesSet) {
        // revalidate the form if we're unsetting
        this.$refs.ppnForm.validate()
      }
    },
    resetForm () {
      this.$refs.ratesForm.reset()

      if (this.$refs.ppnForm) {
        this.$refs.ppnForm.reset()
      }

      this.resetLOSForms()

      // reset the default object back
      this.rate.rateLengthOfStays = []
      this.rate.description = ''
      this.rate.rate = 0
      this.rate.dayOfWeekRates = [[0], [0], [0], [0], [0], [0], [0]]
    },
    save () {
      if (this.$refs.ratesForm.validate() && ((this.$refs.ppnForm && this.$refs.ppnForm.validate()) || !this.$refs.ppnForm)) {
        this.saving = true

        const rateClone = this.clone(this.mutatableRate)
        rateClone.units = this.assignedRooms
        rateClone.rateLengthOfStays = this.rateLengthOfStays

        if (this.isCreate) {
          return this.httpCreateRate({ rate: rateClone, place: this.clone(this.place) }).then(() => {
            // redirect to manage rate page
            this.$router.push({ name: 'place-rates-manage' })

            this.assignedRooms = []

            return Promise.resolve(true)
          })
        } else if (this.isEdit) {
          return this.httpUpdateRate({ rate: rateClone })
            .then(() => {
              this.$router.push({ name: 'place-rates-manage' })

              this.assignedRooms = []

              return Promise.resolve(true)
            })
            .catch(() => Promise.resolve(false))
        }
      } else {
        this.saving = false

        // No Rate name? scroll to field
        if (this.mutatableRate.description === '') {
          this.scrollToTop()
        }

        return Promise.resolve(false)
      }
    },
    scrollToTop: function () {
      document.getElementById('rates-rules-dialog').scrollIntoView({ behavior: 'smooth' })
    },
    openPanel (index) {
      this.panel.push(index)
    },
  },
  mounted () {
    this.rateLengthOfStays = [...this.mutatableRate.rateLengthOfStays]

    if (this.rateSet || this.dayOfWeekRatesSet) {
      this.openPanel(0)
      this.switch1 = true
    }
    if (this.rate.rateLengthOfStays.length > 0) {
      this.openPanel(1)
      this.switch2 = true
    }

    if (this.isDuplicate) {
      this.mutatableRate.description = `${this.mutatableRate.description} (copy)`
    }
  },
  created () {
    // add existing ticked units
    if (this.isEdit || this.isDuplicate) {
      this.rate.units.forEach((unit) => {
        this.assignedRooms.push(unit['@id'])
      })
    }
  },
  watch: {
    rate (newVal) {
      this.mutatableRate = this.clone(newVal)
    },
  },
}
</script>

  <style lang="scss">
  .v-expansion-panels {
    border-radius: 0;
  }
  .v-expansion-panel {
    border-radius: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
  .v-expansion-panel-content__wrap {
    padding: 0 24px 0px;
  }
  .rate-los {
    display: flex;
    align-items: baseline;

    &__field-group {
      display: flex;
      align-items: baseline;
    }

    .v-input {
      width: 75px;
      margin: 0 5px 0 5px;
    }

    .v-select {
      margin: 0 5px 0 5px;
      width: 175px;
    }
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    /* Firefox */
    input[type='number'] {
      -moz-appearance: textfield;
    }
  }

  .v-banner .v-banner__wrapper {
    padding: 5px 0;
  }

  .v-messages.error--text {
    position: absolute;
    width: 100%;
    left: 0;
  }

  // @TODO - MH - these should load auto from tailwind config?
  .border-t-0 {
    border-top: 0;
  }
  .border-opacity-60 {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  </style>
